import { createContext, useState, ReactNode, useContext, useEffect, Dispatch, SetStateAction } from "react";
import Facebook from "../assets/icons/social-icons/facebook.svg?react";
import Instagram from "../assets/icons/social-icons/insta.svg?react";
import X from "../assets/icons/social-icons/x.svg?react";
import Youtube from "../assets/icons/social-icons/youtube.svg?react";

import FacebookLight from "../assets/icons/facebook.svg?react";
import InstagramLight from "../assets/icons/instagram.svg?react";
import XLight from "../assets/icons/x.svg?react";
import YoutubeLight from "../assets/icons/youtube.svg?react";

import Spotify from "../assets/icons/spotify.svg?react";
import Gaana from "../assets/icons/gaana.svg?react";
import JioSaavn from "../assets/icons/jioSaavn.svg?react";
import Wynk from "../assets/icons/wynk.svg?react";
import ApplePodcasts from "../assets/icons/applePodcasts.svg?react";
import { Episode, Media } from "../types/media";
import { SocialMedia } from "../types/index.d";
import { useMedia } from "../hooks/useMedia";
import { getSocialMedia } from "../services/commonService";

interface GlobalContextType {
    socialMedia: SocialMedia[];
    isAudioPlayerVisible: boolean;
    media: Media;
    episode: Episode;
    episodes: Episode[];
    isLoading: boolean;
    error: Error | null;
    setIsAudioPlayerVisible: (isAudioPlayerVisible: boolean) => void;
    setMedia: Dispatch<SetStateAction<Media>>;
    setEpisode: Dispatch<SetStateAction<Episode>>;
    setEpisodes: Dispatch<SetStateAction<Episode[]>>;
    getSocialMediaDetails: (platformId: string | number, className?: string) => { name: string, icon: JSX.Element | null };
}

export const GlobalContext = createContext<GlobalContextType>({} as GlobalContextType);

export const GlobalProvider = ({ children }: { children: ReactNode }) => {
    const [socialMedia, setSocialMedia] = useState<SocialMedia[]>([]);
    const [isAudioPlayerVisible, setIsAudioPlayerVisible] = useState(false);
    const [media, setMedia] = useState<Media>({} as Media);
    const [episode, setEpisode] = useState<Episode>({} as Episode);
    const [episodes, setEpisodes] = useState<Episode[]>([]);
    const { episodes: episodeList, fetchEpisodes } = useMedia('Audio');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (media.id) fetchEpisodes(media.id);
    }, [media.id]);

    useEffect(() => {
        if (episodeList.length>0) {
            setEpisodes(episodeList);
            setEpisode(episodeList[0]);
            setIsAudioPlayerVisible(true)
        }
    }, [episodeList]);

    useEffect(() => {
        fetchSocialMedia();
    }, []);

    const fetchSocialMedia = async () => {
        setIsLoading(true);
        setError(null);
        getSocialMedia().then((response) => {
            setSocialMedia(response.data);
        }).catch(err => {
            const error = err instanceof Error ? err : new Error('Failed to fetch social media');
            setError(error);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    const getSocialMediaDetails = (platformId: string | number, className?: string,darkMode?: boolean) => {
        const platform = socialMedia.find(platform => platform.id === Number(platformId));
        if (!platform) return { name: '', icon: null };

        const iconsMap: { [key: string]: JSX.Element } = {
            "Facebook": <Facebook className={` ${className}`} />,
            "X": <X className={`${className}`} />,
            "Instagram": <Instagram className={`${className}`} />,
            "Youtube": <Youtube className={`${className}`} />,
            "Spotify": <Spotify className={`${className}`} />,
            "Gaana": <Gaana className={`${className}`} />,
            "JioSaavn": <JioSaavn className={`${className}`} />,
            "Wynk": <Wynk className={`${className}`} />,
            "Apple Podcasts": <ApplePodcasts className={`${className}`} />
        };

        const lightIconsMap: { [key: string]: JSX.Element } = {
            "Facebook": <FacebookLight className={`${className}`} />,
            "X": <XLight className={`${className}`} />,
            "Instagram": <InstagramLight className={`${className}`} />,
            "Youtube": <YoutubeLight className={`${className}`} />,
            "Spotify": <Spotify className={`${className}`} />,
            "Gaana": <Gaana className={`${className}`} />,
            "JioSaavn": <JioSaavn className={`${className}`} />,
            "Wynk": <Wynk className={`${className}`} />,
            "Apple Podcasts": <ApplePodcasts className={`${className}`} />
        };

        return { name: platform.name, icon: darkMode ? iconsMap[platform.name] : lightIconsMap[platform.name] || null };
    };

    return <GlobalContext.Provider value={{ socialMedia, isAudioPlayerVisible, media, episode, episodes, isLoading, error, setIsAudioPlayerVisible, setMedia, setEpisode, setEpisodes, getSocialMediaDetails }}>{children}</GlobalContext.Provider>;
};

export const useGlobal = () => {
    const context = useContext(GlobalContext);
    if (!context) {
        throw new Error('useGlobal must be used within a GlobalProvider');
    }
    return context;
};